import { useEffect, useState } from 'react';
import { Avatar } from '@chakra-ui/react';

// One letter, not Chakra's two: the name is usually a first name alone, and a
// workspace is identified by whose it is, not by initials.
const initial = (name) => name.trim().charAt(0).toUpperCase();

/**
 * A workspace's owner, as a round photo: `owner_picture`, the Google avatar
 * stored at sign-in, with the owner's initial when there is none or it does
 * not load. Only the owner's photo is ever shown, never other members'.
 *
 * Pass a listing row or an invitation preview as `workspace`, or `name` and
 * `src` directly. `size` is in px, and the initial scales with it. That takes
 * Chakra's `--avatar-font-size`, not `fontSize`: the initial's own element
 * reads its size from that variable, which the theme's default `md` sets to
 * 1.2rem whatever the box measures, so a `fontSize` on the Avatar never
 * reached it (a 20px avatar held a 19px letter).
 *
 * Google avatar URLs can be refused when the request carries a referrer, so
 * they are requested with `referrerPolicy="no-referrer"`. Chakra's Avatar
 * preloads its `src` without that policy, and would fall back to the initial
 * on a photo that loads fine without a referrer. So the photo is preloaded
 * here, with the policy, and handed to the Avatar only once it has loaded;
 * the Avatar then draws it straight from the cache (`ignoreFallback`), and
 * until then, or when it fails, it draws the initial.
 *
 * The name is `owner_first_name` only, never `owner_full_name`: the backend
 * leaves the first name null when the full name is an address, and the
 * Avatar's initial and label would otherwise be taken from that email.
 *
 * Decorative by default. Every place it sits (switcher rows, the guest flow's
 * header and Done card, the accept card) already names the owner in text, so
 * announcing the photo too made a screen reader read the owner twice; and a
 * nameless one was announced by Chakra's built-in English " avatar". Pass
 * `announce` where the photo is the only thing saying whose space it is: it
 * is then labelled with the owner's name, and stays hidden when there is none.
 */
export default function OwnerAvatar({
  workspace, name, src, size = 32, announce = false, sx, ...rest
}) {
  const ownerName = name ?? (workspace?.owner_first_name || null);
  const photo = src ?? workspace?.owner_picture ?? null;
  const [loaded, setLoaded] = useState(null);

  useEffect(() => {
    if (!photo) return undefined;
    let live = true;
    const img = new window.Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => { if (live) setLoaded(photo); };
    img.onerror = () => { if (live) setLoaded(null); };
    img.src = photo;
    return () => { live = false; };
  }, [photo]);

  const shown = photo && loaded === photo ? photo : undefined;
  return (
    <Avatar
      name={ownerName || undefined}
      src={shown}
      ignoreFallback={!!shown}
      referrerPolicy="no-referrer"
      getInitials={initial}
      // Never Chakra's untranslated default; a nameless avatar is hidden anyway.
      iconLabel=""
      aria-hidden={announce && ownerName ? undefined : true}
      boxSize={`${size}px`}
      fontWeight={700}
      // Opaque, not a translucent tint: on the accept card the disc overlaps
      // the app icon, and a see-through fill let the dark icon show behind
      // the letter (about 1.2:1).
      bg="redesign.bg.accent"
      color="redesign.heading"
      flexShrink={0}
      {...rest}
      sx={{ '--avatar-font-size': `${Math.round(size * 0.42)}px`, ...sx }}
    />
  );
}
